<script lang="ts">
  /**
   * Formulario de premissas do diagnostico.
   *
   * Carrega valores atuais do store (não começa zerado se já houver
   * premissas salvas) e mostra confirmação visual ao salvar.
   */
  import ErrorPanel from '../../components/ErrorPanel.svelte';
  import Button from '../../components/Button.svelte';
  import { appStore } from '../../app/store';
  import {
    DEFAULT_TOLERANCE_MINUTES,
    DEFAULT_NIGHT_HOUR_START,
    DEFAULT_NIGHT_HOUR_END,
    DEFAULT_DAILY_HOURS,
    DEFAULT_MONTHLY_HOURS,
    DEFAULT_EXCESSIVE_JOURNEY_MINUTES,
    DEFAULT_INTRADAY_BREAK_MINUTES,
    DEFAULT_INTERJOURNEY_MINUTES,
    DEFAULT_CORRECTION_MINUTES_PER_EVENT,
    DEFAULT_PRE_ASSIGNED_LUNCH_MINUTES,
  } from '../../app/constants';
  import { parseCurrencyInput } from '../../core/format/currencyInput';
  import { validateAssumptions } from './validateAssumptions';
  import type { ValidationIssue } from '../../core/validation/validation.types';

  const { state } = appStore;

  // Valores iniciais vêm do store (preserva o que o usuário já configurou)
  const current = $state.assumptions;

  // Form fields (raw strings for currency inputs; numbers for the rest)
  let hourlyRateStr = current.hourlyRateBrl !== undefined ? String(current.hourlyRateBrl) : '';
  let salaryStr = current.averageGrossSalaryBrl !== undefined
    ? String(current.averageGrossSalaryBrl)
    : (current.monthlyPayrollBrl !== undefined ? String(current.monthlyPayrollBrl) : '');
  let dailyHours: number = current.dailyHours ?? DEFAULT_DAILY_HOURS;
  let monthlyHours: number = current.monthlyHours ?? DEFAULT_MONTHLY_HOURS;
  let toleranceMinutes: number = current.toleranceMinutes ?? DEFAULT_TOLERANCE_MINUTES;
  let excessiveJourneyMinutes: number = current.excessiveJourneyMinutes ?? DEFAULT_EXCESSIVE_JOURNEY_MINUTES;
  let shortIntradayBreakMinutes: number = current.shortIntradayBreakMinutes ?? DEFAULT_INTRADAY_BREAK_MINUTES;
  let shortInterjourneyMinutes: number = current.shortInterjourneyMinutes ?? DEFAULT_INTERJOURNEY_MINUTES;
  let nightStartHour: number = current.nightStartHour ?? DEFAULT_NIGHT_HOUR_START;
  let nightEndHour: number = current.nightEndHour ?? DEFAULT_NIGHT_HOUR_END;
  let correctionMinutesPerEvent: number = current.correctionMinutesPerEvent ?? DEFAULT_CORRECTION_MINUTES_PER_EVENT;
  let preAssignedLunchMinutes: number = current.preAssignedLunchMinutes ?? DEFAULT_PRE_ASSIGNED_LUNCH_MINUTES;

  let submitted = false;
  let savedAt: number | null = null;
  let fieldErrors: Record<string, string> = {};

  function parseHourlyRate(): number | undefined {
    if (!hourlyRateStr.trim()) return undefined;
    const v = parseCurrencyInput(hourlyRateStr);
    return v === null ? undefined : v;
  }

  function parseSalary(): number | undefined {
    if (!salaryStr.trim()) return undefined;
    const v = parseCurrencyInput(salaryStr);
    return v === null ? undefined : v;
  }

  /**
   * Sincronização bidirecional valor-hora ↔ salário bruto:
   * - Quando o usuário digita em "valor-hora", o salário é recalculado.
   * - Quando o usuário digita em "salário", o valor-hora é recalculado.
   * - Quando "horas mensais previstas" muda, o campo NÃO editado pelo usuário
   *   por último é recalculado pra preservar a relação.
   *
   * Usamos `lastEdited` pra saber qual campo é fonte da verdade no momento
   * e qual é derivado. Bind:value continua atualizando o valor digitado;
   * `on:input` chama o sync e marca `lastEdited`.
   */
  let lastEdited: 'hourly' | 'salary' | null =
    current.hourlyRateBrl !== undefined ? 'hourly'
    : (current.averageGrossSalaryBrl !== undefined || current.monthlyPayrollBrl !== undefined) ? 'salary'
    : null;

  /** Formata número como BRL com 2 casas e vírgula decimal (sem prefixo). */
  function formatMoneyValue(v: number): string {
    return v.toFixed(2).replace('.', ',');
  }

  function syncFromHourly(): void {
    lastEdited = 'hourly';
    const v = parseCurrencyInput(hourlyRateStr);
    if (v === null || v <= 0) return;
    const hours = Number.isFinite(monthlyHours) && monthlyHours > 0 ? monthlyHours : DEFAULT_MONTHLY_HOURS;
    salaryStr = formatMoneyValue(v * hours);
  }

  function syncFromSalary(): void {
    lastEdited = 'salary';
    const v = parseCurrencyInput(salaryStr);
    if (v === null || v <= 0) return;
    const hours = Number.isFinite(monthlyHours) && monthlyHours > 0 ? monthlyHours : DEFAULT_MONTHLY_HOURS;
    hourlyRateStr = formatMoneyValue(v / hours);
  }

  /** Recalcula o lado derivado quando `monthlyHours` muda. */
  $: if (Number.isFinite(monthlyHours) && monthlyHours > 0 && lastEdited !== null) {
    if (lastEdited === 'hourly') {
      const v = parseCurrencyInput(hourlyRateStr);
      if (v !== null && v > 0) {
        const newSalary = formatMoneyValue(v * monthlyHours);
        if (newSalary !== salaryStr) salaryStr = newSalary;
      }
    } else {
      const v = parseCurrencyInput(salaryStr);
      if (v !== null && v > 0) {
        const newHourly = formatMoneyValue(v / monthlyHours);
        if (newHourly !== hourlyRateStr) hourlyRateStr = newHourly;
      }
    }
  }

  // Valor-hora "efetivo" mostrado abaixo do salário (mesmo cálculo de derivação
  // pra exibição/auditoria, agora que os campos estão sincronizados).
  $: derivedHourlyRate = (() => {
    const v = parseHourlyRate();
    if (v !== undefined && v > 0) return v;
    const salary = parseSalary();
    if (salary === undefined || salary <= 0) return null;
    const hours = Number.isFinite(monthlyHours) && monthlyHours > 0 ? monthlyHours : DEFAULT_MONTHLY_HOURS;
    return salary / hours;
  })();

  function buildAssumptions() {
    return {
      hourlyRateBrl: parseHourlyRate(),
      averageGrossSalaryBrl: parseSalary(),
      dailyHours: Number.isFinite(dailyHours) ? dailyHours : DEFAULT_DAILY_HOURS,
      monthlyHours: Number.isFinite(monthlyHours) ? monthlyHours : DEFAULT_MONTHLY_HOURS,
      toleranceMinutes: Number.isFinite(toleranceMinutes) ? toleranceMinutes : DEFAULT_TOLERANCE_MINUTES,
      excessiveJourneyMinutes: Number.isFinite(excessiveJourneyMinutes) ? excessiveJourneyMinutes : DEFAULT_EXCESSIVE_JOURNEY_MINUTES,
      shortIntradayBreakMinutes: Number.isFinite(shortIntradayBreakMinutes) ? shortIntradayBreakMinutes : DEFAULT_INTRADAY_BREAK_MINUTES,
      shortInterjourneyMinutes: Number.isFinite(shortInterjourneyMinutes) ? shortInterjourneyMinutes : DEFAULT_INTERJOURNEY_MINUTES,
      nightStartHour,
      nightEndHour,
      correctionMinutesPerEvent: Number.isFinite(correctionMinutesPerEvent) ? correctionMinutesPerEvent : DEFAULT_CORRECTION_MINUTES_PER_EVENT,
      preAssignedLunchMinutes: Number.isFinite(preAssignedLunchMinutes) ? preAssignedLunchMinutes : DEFAULT_PRE_ASSIGNED_LUNCH_MINUTES,
    };
  }

  // Recomputa assumptions sempre que qualquer campo do formulário muda.
  // Refs explícitas garantem que Svelte invalide o reativo (a análise estática
  // só rastreia refs diretas, não vê as variáveis lidas dentro de buildAssumptions).
  $: assumptions = ((
    _h = hourlyRateStr,
    _s = salaryStr,
    _dh = dailyHours,
    _mh = monthlyHours,
    _tol = toleranceMinutes,
    _exc = excessiveJourneyMinutes,
    _ib = shortIntradayBreakMinutes,
    _ij = shortInterjourneyMinutes,
    _ns = nightStartHour,
    _ne = nightEndHour,
    _cme = correctionMinutesPerEvent,
    _pal = preAssignedLunchMinutes,
  ) => {
    void _h; void _s; void _dh; void _mh; void _tol;
    void _exc; void _ib; void _ij; void _ns; void _ne; void _cme; void _pal;
    return buildAssumptions();
  })();
  $: issues = submitted ? validateAssumptions(assumptions) : [];
  $: justSaved = savedAt !== null && Date.now() - savedAt < 4000;

  // Auto-commit ao store: "Iniciar análise" usa sempre o valor digitado,
  // mesmo sem o consultor clicar "Salvar premissas".
  $: appStore.updateAssumptions(assumptions);

  function handleSubmit() {
    submitted = true;
    fieldErrors = {};

    // Validate currency fields (show inline errors without blocking)
    if (hourlyRateStr.trim() && parseCurrencyInput(hourlyRateStr) === null) {
      fieldErrors['hourlyRate'] = 'Formato invalido. Use "25,50" ou "R$ 25,50".';
    }
    if (salaryStr.trim() && parseCurrencyInput(salaryStr) === null) {
      fieldErrors['salary'] = 'Formato invalido. Use "3500,00" ou "R$ 3.500,00".';
    }

    const validatedAssumptions = buildAssumptions();
    const validationIssues = validateAssumptions(validatedAssumptions);

    // Only block on INVALID_TOLERANCE with negative value (INVALID_HOURLY_RATE / INVALID_PAYROLL
    // come from bad parse which we handle via fieldErrors, not blocking submit)
    const hasBlockingError = validationIssues.some(
      i => i.severity === 'error' && i.code === 'INVALID_TOLERANCE'
    );

    if (!hasBlockingError) {
      // Store ja foi atualizado pelo auto-commit reativo (linha 168);
      // aqui so registramos o timestamp pra UI mostrar feedback "Salvo".
      savedAt = Date.now();
    }
  }

  function issuesForPanel(): Array<{ severity: 'error' | 'warning' | 'info'; message: string }> {
    return (issues as ValidationIssue[])
      .filter(i => i.severity === 'error' || i.severity === 'warning' || i.severity === 'info')
      .map(i => ({
        severity: i.severity as 'error' | 'warning' | 'info',
        message: i.message,
      }));
  }
</script>

<form class="assumptions-form" on:submit|preventDefault={handleSubmit} novalidate>
  <h2 class="assumptions-form__title">Premissas do Diagnóstico</h2>
  <p class="assumptions-form__subtitle">
    Configure os parâmetros que serão usados para calcular métricas e ROI.
    Campos de custo são opcionais — sem eles, o cálculo de impacto financeiro ficará oculto.
  </p>

  <!-- Custo section -->
  <fieldset class="assumptions-form__section">
    <legend class="assumptions-form__section-title">Custo (opcional)</legend>

    <div class="assumptions-form__field">
      <label class="assumptions-form__label" for="hourly-rate">
        Custo médio por hora (BRL)
      </label>
      <input
        id="hourly-rate"
        class="assumptions-form__input"
        class:assumptions-form__input--error={!!fieldErrors['hourlyRate']}
        type="text"
        bind:value={hourlyRateStr}
        on:input={syncFromHourly}
        placeholder="Ex: R$ 25,50"
        aria-describedby="hourly-rate-help"
      />
      <span id="hourly-rate-help" class="assumptions-form__help">
        Opcional. Inclua encargos (INSS, FGTS etc.). Atualiza o salário automaticamente quando preenchido.
      </span>
      {#if fieldErrors['hourlyRate']}
        <span class="assumptions-form__field-error" role="alert">{fieldErrors['hourlyRate']}</span>
      {/if}
    </div>

    <div class="assumptions-form__field">
      <label class="assumptions-form__label" for="salary">
        Salário bruto médio mensal (BRL)
      </label>
      <input
        id="salary"
        class="assumptions-form__input"
        class:assumptions-form__input--error={!!fieldErrors['salary']}
        type="text"
        bind:value={salaryStr}
        on:input={syncFromSalary}
        placeholder="Ex: R$ 3.500,00"
        aria-describedby="salary-help"
      />
      <span id="salary-help" class="assumptions-form__help">
        Atualiza o valor-hora automaticamente quando preenchido. Cálculo: salário ÷ horas mensais previstas.
        {#if derivedHourlyRate !== null}
          <strong class="assumptions-form__derived-rate"> ≈ R$ {derivedHourlyRate.toFixed(2)}/h estimado</strong>
        {/if}
      </span>
      {#if fieldErrors['salary']}
        <span class="assumptions-form__field-error" role="alert">{fieldErrors['salary']}</span>
      {/if}
    </div>
  </fieldset>

  <!-- Jornada section -->
  <fieldset class="assumptions-form__section">
    <legend class="assumptions-form__section-title">Parâmetros de Jornada</legend>

    <div class="assumptions-form__row">
      <div class="assumptions-form__field">
        <label class="assumptions-form__label" for="daily-hours">
          Horas diárias previstas (h)
        </label>
        <input
          id="daily-hours"
          class="assumptions-form__input"
          type="number"
          min="1"
          max="12"
          step="0.5"
          bind:value={dailyHours}
          aria-describedby="daily-hours-help"
        />
        <span id="daily-hours-help" class="assumptions-form__help">
          Padrão: {DEFAULT_DAILY_HOURS} h (CLT 44h/sem). Usado como escala de fallback quando o AFD não trouxer escalas.
        </span>
      </div>

      <div class="assumptions-form__field">
        <label class="assumptions-form__label" for="monthly-hours">
          Horas mensais previstas (h)
        </label>
        <input
          id="monthly-hours"
          class="assumptions-form__input"
          type="number"
          min="1"
          max="400"
          step="1"
          bind:value={monthlyHours}
          aria-describedby="monthly-hours-help"
        />
        <span id="monthly-hours-help" class="assumptions-form__help">
          Padrão: {DEFAULT_MONTHLY_HOURS} h. Usado para extrapolar custo mensal e derivar valor-hora a partir da folha.
        </span>
      </div>
    </div>

    <div class="assumptions-form__field">
      <label class="assumptions-form__label" for="tolerance">
        Tolerância de atraso (minutos)
      </label>
      <input
        id="tolerance"
        class="assumptions-form__input"
        type="number"
        min="0"
        max="120"
        bind:value={toleranceMinutes}
        aria-describedby="tolerance-help"
      />
      <span id="tolerance-help" class="assumptions-form__help">
        Padrão: {DEFAULT_TOLERANCE_MINUTES} min. Tempo descontado antes de contabilizar atraso ou saída antecipada.
      </span>
    </div>

    <div class="assumptions-form__field">
      <label class="assumptions-form__label" for="excessive-journey">
        Jornada excessiva (minutos/dia)
      </label>
      <input
        id="excessive-journey"
        class="assumptions-form__input"
        type="number"
        min="60"
        max="1440"
        bind:value={excessiveJourneyMinutes}
        aria-describedby="excessive-journey-help"
      />
      <span id="excessive-journey-help" class="assumptions-form__help">
        Padrão: {DEFAULT_EXCESSIVE_JOURNEY_MINUTES} min ({Math.round(DEFAULT_EXCESSIVE_JOURNEY_MINUTES / 60)} h). Jornadas acima deste limite serão sinalizadas.
      </span>
    </div>

    <div class="assumptions-form__field">
      <label class="assumptions-form__label" for="intraday-break">
        Intervalo intrajornada mínimo (minutos)
      </label>
      <input
        id="intraday-break"
        class="assumptions-form__input"
        type="number"
        min="0"
        max="240"
        bind:value={shortIntradayBreakMinutes}
        aria-describedby="intraday-break-help"
      />
      <span id="intraday-break-help" class="assumptions-form__help">
        Padrão: {DEFAULT_INTRADAY_BREAK_MINUTES} min (CLT: 1h para jornadas acima de 6h). Intervalos menores serão sinalizados como insuficientes.
      </span>
    </div>

    <div class="assumptions-form__field">
      <label class="assumptions-form__label" for="interjourney">
        Interjornada mínima (minutos)
      </label>
      <input
        id="interjourney"
        class="assumptions-form__input"
        type="number"
        min="0"
        max="1440"
        bind:value={shortInterjourneyMinutes}
        aria-describedby="interjourney-help"
      />
      <span id="interjourney-help" class="assumptions-form__help">
        Padrão: {DEFAULT_INTERJOURNEY_MINUTES} min ({Math.round(DEFAULT_INTERJOURNEY_MINUTES / 60)} h). Intervalo mínimo entre o fim de uma jornada e o início da próxima.
      </span>
    </div>

    <div class="assumptions-form__field">
      <label class="assumptions-form__label" for="pre-assigned-lunch">
        Almoço pré-assinalado (minutos)
      </label>
      <input
        id="pre-assigned-lunch"
        class="assumptions-form__input"
        type="number"
        min="0"
        max="120"
        bind:value={preAssignedLunchMinutes}
        aria-describedby="pre-assigned-lunch-help"
      />
      <span id="pre-assigned-lunch-help" class="assumptions-form__help">
        Padrão: {DEFAULT_PRE_ASSIGNED_LUNCH_MINUTES}. Quando colaboradores não batem ponto de almoço, sistema desconta esse valor da jornada bruta automaticamente. 60 min para empresas CLT padrão.
      </span>
    </div>
  </fieldset>

  <!-- Operação administrativa -->
  <fieldset class="assumptions-form__section">
    <legend class="assumptions-form__section-title">Esforço de Correção</legend>

    <div class="assumptions-form__field">
      <label class="assumptions-form__label" for="correction-minutes">
        Tempo médio por correção manual (minutos)
      </label>
      <input
        id="correction-minutes"
        class="assumptions-form__input"
        type="number"
        min="0"
        max="60"
        step="0.5"
        bind:value={correctionMinutesPerEvent}
        aria-describedby="correction-minutes-help"
      />
      <span id="correction-minutes-help" class="assumptions-form__help">
        Padrão: {DEFAULT_CORRECTION_MINUTES_PER_EVENT} min. Tempo estimado que o RH/DP gasta em cada correção manual de marcação. Usado para estimar o esforço administrativo de fechamento da folha.
      </span>
    </div>
  </fieldset>

  <!-- Periodo noturno section -->
  <fieldset class="assumptions-form__section">
    <legend class="assumptions-form__section-title">Período Noturno</legend>

    <div class="assumptions-form__row">
      <div class="assumptions-form__field">
        <label class="assumptions-form__label" for="night-start">
          Início do período noturno (hora)
        </label>
        <input
          id="night-start"
          class="assumptions-form__input assumptions-form__input--sm"
          type="number"
          min="0"
          max="23"
          bind:value={nightStartHour}
          aria-describedby="night-start-help"
        />
        <span id="night-start-help" class="assumptions-form__help">
          Padrão: {DEFAULT_NIGHT_HOUR_START}h
        </span>
      </div>

      <div class="assumptions-form__field">
        <label class="assumptions-form__label" for="night-end">
          Fim do período noturno (hora)
        </label>
        <input
          id="night-end"
          class="assumptions-form__input assumptions-form__input--sm"
          type="number"
          min="0"
          max="23"
          bind:value={nightEndHour}
          aria-describedby="night-end-help"
        />
        <span id="night-end-help" class="assumptions-form__help">
          Padrão: {DEFAULT_NIGHT_HOUR_END}h
        </span>
      </div>
    </div>
  </fieldset>

  {#if submitted && issues.length > 0}
    <div class="assumptions-form__issues">
      <ErrorPanel issues={issuesForPanel()} />
    </div>
  {/if}

  <div class="assumptions-form__actions">
    {#if justSaved}
      <span class="assumptions-form__saved" role="status" aria-live="polite">
        ✓ Premissas salvas
      </span>
    {/if}
    <Button type="submit" variant="primary">Salvar premissas</Button>
  </div>
</form>

<style>
  .assumptions-form {
    display: flex;
    flex-direction: column;
    gap: var(--ptt-space-5);
    max-width: 640px;
  }

  .assumptions-form__title {
    font-family: var(--ptt-font-sans);
    font-size: 1.25rem;
    font-weight: 700;
    color: var(--ptt-gray-400);
    margin: 0;
  }

  .assumptions-form__subtitle {
    font-family: var(--ptt-font-sans);
    font-size: 0.9375rem;
    color: var(--ptt-gray-200);
    margin: 0;
    line-height: 1.5;
  }

  .assumptions-form__section {
    border: 1px solid var(--ptt-gray-20);
    border-radius: var(--ptt-radius-md);
    padding: var(--ptt-space-4) var(--ptt-space-5);
    display: flex;
    flex-direction: column;
    gap: var(--ptt-space-4);
  }

  .assumptions-form__section-title {
    font-family: var(--ptt-font-sans);
    font-size: 0.875rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--ptt-gray-200);
    padding: 0 var(--ptt-space-1);
  }

  .assumptions-form__field {
    display: flex;
    flex-direction: column;
    gap: var(--ptt-space-1);
  }

  .assumptions-form__row {
    display: flex;
    gap: var(--ptt-space-5);
    flex-wrap: wrap;
  }

  .assumptions-form__row .assumptions-form__field {
    flex: 1;
    min-width: 160px;
  }

  .assumptions-form__label {
    font-family: var(--ptt-font-sans);
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--ptt-gray-300);
  }

  .assumptions-form__input {
    height: 2.5rem;
    padding: 0 var(--ptt-space-3);
    border: 1.5px solid var(--ptt-gray-30);
    border-radius: var(--ptt-radius-sm);
    font-family: var(--ptt-font-sans);
    font-size: 0.9375rem;
    color: var(--ptt-gray-400);
    background: var(--ptt-white);
    transition: border-color 0.15s ease;
    width: 100%;
    box-sizing: border-box;
  }

  .assumptions-form__input:focus {
    outline: none;
    border-color: var(--ptt-purple-100);
    box-shadow: var(--ptt-focus-ring);
  }

  .assumptions-form__input--error {
    border-color: var(--ptt-orange-100);
  }

  .assumptions-form__input--sm {
    max-width: 100px;
  }

  .assumptions-form__help {
    font-family: var(--ptt-font-sans);
    font-size: 0.8125rem;
    color: var(--ptt-gray-200);
    line-height: 1.4;
  }

  .assumptions-form__field-error {
    font-family: var(--ptt-font-sans);
    font-size: 0.8125rem;
    color: var(--ptt-orange-100);
    font-weight: 600;
  }

  .assumptions-form__derived-rate {
    color: var(--ptt-purple-100);
    font-size: 0.8125rem;
    font-weight: 700;
    display: inline;
  }

  .assumptions-form__issues {
    padding: var(--ptt-space-1) 0;
  }

  .assumptions-form__actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: var(--ptt-space-3);
    padding-top: var(--ptt-space-2);
  }

  .assumptions-form__saved {
    color: #2e7d32;
    font-size: 0.9375rem;
    font-weight: 600;
    animation: fade-in 0.2s ease;
  }

  @keyframes fade-in {
    from { opacity: 0; transform: translateY(-2px); }
    to { opacity: 1; transform: translateY(0); }
  }
</style>
